/**
 * AnalyzeFitCost.jsx — RC 1.38.B.1 Commercial Transparency (corrected).
 *
 * Two-tier label for the Analyse Fit action:
 *
 *   1. While the user has remaining free Analyze-Fit allowance for
 *      the current daily window → "· Free (N left today)".
 *   2. Once exhausted → falls back to the regular per-action usage
 *      cost from `/api/account/plan .usage_costs.analyze_fit`.
 *
 * Source endpoints (real, RC 1.33.B+):
 *   GET /api/account/plan               (cost + plan + balances)
 *   GET /api/jobs/analyze-fit/usage     (daily free remaining)
 *
 * No legacy `/api/credits/me`. No hardcoded values.
 */

import {
  useCredits, getActionCost, isInsufficient, totalUsageRemaining, isPaidPlan,
} from '@/lib/creditsContext';

export default function AnalyzeFitCost({
  className = '',
  prefix = '· ',
  showShortfall = true,
}) {
  const {
    usageCosts, planCode, includedUsageRemaining, extraUsageRemaining, freeFit,
  } = useCredits();
  const cost = getActionCost(usageCosts, 'analyze_fit');
  const remaining = typeof freeFit?.remaining === 'number' ? freeFit.remaining : null;

  // Free tier still has allowance → free.
  // RC 1.42 P0.2-followup — paid plans (focus/flow) do NOT get the
  // free-tier daily allowance; their plan-included usage is the
  // substitute. Without this guard the badge would advertise
  // "Free (N left today)" to a Flow-plan user whose backend
  // analyse-fit click will be charged live Credits and 429-rejected
  // when their balance is 0 — exactly the trust gap the operator
  // surfaced via screenshot on 2026-04-29.
  if (remaining != null && remaining > 0 && !isPaidPlan(planCode)) {
    return (
      <span
        className={`inline-flex items-baseline gap-1 ${className}`}
        data-testid="analyze-fit-cost-free"
        data-remaining-free={remaining}
      >
        <span>
          {prefix}
          <span className="text-ndor-success font-mono-ui font-semibold">Free</span>
          <span className="text-ndor-ink-muted ml-1">
            ({remaining} left today)
          </span>
        </span>
      </span>
    );
  }

  // No allowance left and no cost loaded yet → render nothing
  // rather than mislead.
  if (cost == null) return null;

  const insufficient = showShortfall && isInsufficient({
    planCode, includedUsageRemaining, extraUsageRemaining, cost,
  });
  const total = isPaidPlan(planCode)
    ? totalUsageRemaining({ includedUsageRemaining, extraUsageRemaining })
    : null;

  return (
    <span
      className={`inline-flex items-baseline gap-1 ${className}`}
      data-testid="analyze-fit-cost-paid"
      data-cost={cost}
      data-insufficient={insufficient ? 'true' : 'false'}
    >
      <span>
        {prefix}
        <span className="font-mono-ui font-semibold">{cost}</span>{' '}
        {cost === 1 ? 'Credit' : 'Credits'}
      </span>
      {insufficient && typeof total === 'number' && (
        <span
          className="text-ndor-warning text-[11px] font-mono-ui"
          data-testid="analyze-fit-cost-shortfall"
        >
          (you have {total})
        </span>
      )}
    </span>
  );
}
